/**
 * Gamepad polling through the Gamepad API's `standard` mapping, with a
 * controller family table (Xbox, PlayStation, Nintendo Switch) and a
 * per-device fallback for pads the browser does not map. Buttons are read by
 * physical position: buttons[0] is always the bottom face button.
 */

import { DEFAULT_PAD_SCHEME, type PadScheme, padScheme } from "./schemes";

export type ControllerFamily = "xbox" | "playstation" | "switch" | "unknown";

/** Standard mapping indices. */
export const STD = {
  bottom: 0,
  right: 1,
  left: 2,
  top: 3,
  lb: 4,
  rb: 5,
  lt: 6,
  rt: 7,
  back: 8,
  start: 9,
  lstick: 10,
  rstick: 11,
  dpadUp: 12,
  dpadDown: 13,
  dpadLeft: 14,
  dpadRight: 15,
  home: 16,
} as const;

/** How a raw (non-standard) pad's indices map onto the standard layout. */
export interface DeviceMapping {
  axes: { lx: number; ly: number; rx: number; ry: number };
  buttons: Partial<Record<keyof typeof STD, number>>;
  /** Triggers reported as axes (the Linux xpad driver does this): axis index and range. */
  triggerAxes?: { lt: number; rt: number; from: number; to: number };
  /** D-pad reported as a hat on two axes (xpad): axis indices, y positive is down. */
  dpadAxes?: { x: number; y: number };
}

/**
 * Raw layout of the Linux xpad driver as the Gamepad API exposes it when the browser does not apply
 * the standard mapping (unknown vendor ids such as GameSir): triggers are axes 2 and 5 resting at -1,
 * the right stick is axes 3 and 4, the d-pad is a hat on axes 6 and 7.
 */
export const xpadRaw: DeviceMapping = {
  axes: { lx: 0, ly: 1, rx: 3, ry: 4 },
  buttons: {
    bottom: 0,
    right: 1,
    left: 2,
    top: 3,
    lb: 4,
    rb: 5,
    back: 6,
    start: 7,
    home: 8,
    lstick: 9,
    rstick: 10,
  },
  triggerAxes: { lt: 2, rt: 5, from: -1, to: 1 },
  dpadAxes: { x: 6, y: 7 },
};

/** Keyed by "vendor:product" (lowercase hex). Used only when mapping !== "standard". */
export const deviceMappings: Record<string, DeviceMapping> = {
  // GameSir G7 SE (Xbox-licensed, wired) through xpad.
  "3537:1010": xpadRaw,
  // Nintendo Switch Pro Controller through hid-nintendo on Linux, raw layout.
  "057e:2009": {
    axes: { lx: 0, ly: 1, rx: 2, ry: 3 },
    buttons: {
      bottom: 0,
      right: 1,
      left: 2,
      top: 3,
      lb: 4,
      rb: 5,
      lt: 6,
      rt: 7,
      back: 8,
      start: 9,
      lstick: 10,
      rstick: 11,
      dpadUp: 12,
      dpadDown: 13,
      dpadLeft: 14,
      dpadRight: 15,
      home: 16,
    },
  },
  // Joy-Con pair combined by joycond. Chromium exposes the virtual evdev buttons in Linux key-code
  // order: B, A, X, Y, Capture, L, R, ZL, ZR, Minus, Plus, Home, LS, RS, then the d-pad.
  // This is product 2008 (the id joycond assigns to its virtual pair), not product 200e (the
  // physical charging grip).
  "057e:2008": {
    axes: { lx: 0, ly: 1, rx: 2, ry: 3 },
    buttons: {
      bottom: 0,
      right: 1,
      top: 2,
      left: 3,
      lb: 5,
      rb: 6,
      lt: 7,
      rt: 8,
      back: 9,
      start: 10,
      home: 11,
      lstick: 12,
      rstick: 13,
      dpadUp: 14,
      dpadDown: 15,
      dpadLeft: 16,
      dpadRight: 17,
    },
  },
  // Joy-Cons in the physical charging grip.
  "057e:200e": {
    axes: { lx: 0, ly: 1, rx: 2, ry: 3 },
    buttons: {
      bottom: 0,
      right: 1,
      left: 2,
      top: 3,
      lb: 4,
      rb: 5,
      lt: 6,
      rt: 7,
      back: 8,
      start: 9,
      lstick: 10,
      rstick: 11,
      dpadUp: 12,
      dpadDown: 13,
      dpadLeft: 14,
      dpadRight: 15,
      home: 16,
    },
  },
};

export interface PadIdentity {
  vendor: string;
  product: string;
  family: ControllerFamily;
  /** True when the pad's triggers are digital (Switch). */
  digitalTriggers: boolean;
}

export function identifyPad(id: string): PadIdentity {
  const lower = id.toLowerCase();
  const vm = /vendor:\s*([0-9a-f]{4})/i.exec(id);
  const pm = /product:\s*([0-9a-f]{4})/i.exec(id);
  const vendor = vm ? vm[1]!.toLowerCase() : "";
  const product = pm ? pm[1]!.toLowerCase() : "";
  let family: ControllerFamily = "unknown";
  if (vendor === "057e") family = "switch";
  else if (vendor === "054c") family = "playstation";
  else if (vendor === "045e") family = "xbox";
  else if (lower.includes("pro controller") || lower.includes("joy-con") || lower.includes("nintendo")) {
    family = "switch";
  } else if (lower.includes("xbox") || lower.includes("x-box") || lower.includes("xinput")) {
    family = "xbox";
  } else if (
    lower.includes("dualshock") ||
    lower.includes("dualsense") ||
    lower.startsWith("wireless controller")
  ) {
    family = "playstation";
  }
  return { vendor, product, family, digitalTriggers: family === "switch" };
}

/** Raw snapshot of one pad, for the raw input panel. Fixed size, updated in place. */
export class PadSnapshot {
  connected = false;
  id = "";
  mapping = "";
  index = -1;
  family: ControllerFamily = "unknown";
  digitalTriggers = false;
  readonly axes = new Float64Array(8);
  axisCount = 0;
  readonly buttons = new Float64Array(24);
  readonly pressed = new Uint8Array(24);
  buttonCount = 0;
  /** Time the pad was last read with a fresh timestamp. */
  timestamp = 0;
}

export interface PadFrame {
  lx: number;
  ly: number;
  rx: number;
  ry: number;
  reach: number;
  pull: number;
  /** Left trigger value, raw; the scheme decides whether it pulls or tucks. */
  lt: number;
  jump: boolean;
  tuck: boolean;
  restart: boolean;
  pause: boolean;
  dpadX: number;
  dpadY: number;
  /** Bumpers and stick clicks, raw; the scheme in source.ts decides what they do. */
  lb: boolean;
  rb: boolean;
  l3: boolean;
  r3: boolean;
}

export class GamepadReader {
  readonly snapshot = new PadSnapshot();
  readonly frame: PadFrame = {
    lx: 0,
    ly: 0,
    rx: 0,
    ry: 0,
    reach: 0,
    pull: 0,
    lt: 0,
    jump: false,
    tuck: false,
    restart: false,
    pause: false,
    dpadX: 0,
    dpadY: 0,
    lb: false,
    rb: false,
    l3: false,
    r3: false,
  };
  /** performance.now() of the last button edge seen while polling, for the latency probe. */
  lastEdgeTime = 0;
  /**
   * Which pad this reader follows: the Gamepad API index of one pad (a seat in a split-screen race,
   * bound when its player pressed a button on it), or -1 for the `slot`-th connected pad in the
   * order the browser lists them (0, the default, is the first pad plugged in; a negative slot
   * follows no pad, for a seat that holds the keyboard alone).
   */
  padIndex: number;
  slot: number;
  private prevButtons = new Uint8Array(24);
  private getPads: () => (Gamepad | null)[];

  constructor(getPads?: () => (Gamepad | null)[], padIndex = -1, slot = 0) {
    this.padIndex = padIndex;
    this.slot = slot;
    this.getPads =
      getPads ??
      (() => (typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : []));
  }

  /** Poll the pad this reader follows (see `padIndex`). Returns true if a pad was read. */
  poll(now: number): boolean {
    const pads = this.getPads();
    let pad: Gamepad | null = null;
    let seen = 0;
    for (const p of pads) {
      if (!p?.connected) continue;
      if (this.padIndex >= 0) {
        if (p.index === this.padIndex) {
          pad = p;
          break;
        }
        continue;
      }
      if (seen === this.slot) {
        pad = p;
        break;
      }
      seen++;
    }
    const snap = this.snapshot;
    if (!pad) {
      if (snap.connected) this.clear();
      return false;
    }
    if (!snap.connected || snap.index !== pad.index || snap.id !== pad.id) {
      // A replacement can arrive between polls without an observed disconnected frame.
      this.prevButtons.fill(0);
      const ident = identifyPad(pad.id);
      snap.connected = true;
      snap.id = pad.id;
      snap.mapping = pad.mapping;
      snap.index = pad.index;
      snap.family = ident.family;
      snap.digitalTriggers = ident.digitalTriggers;
    }
    snap.timestamp = pad.timestamp;
    snap.axisCount = Math.min(pad.axes.length, snap.axes.length);
    // Fixed buffers outlive devices; controls absent from this poll must be neutral.
    snap.axes.fill(0, snap.axisCount);
    for (let i = 0; i < snap.axisCount; i++) snap.axes[i] = pad.axes[i] ?? 0;
    snap.buttonCount = Math.min(pad.buttons.length, snap.buttons.length);
    snap.buttons.fill(0, snap.buttonCount);
    snap.pressed.fill(0, snap.buttonCount);
    this.prevButtons.fill(0, snap.buttonCount);
    for (let i = 0; i < snap.buttonCount; i++) {
      const b = pad.buttons[i];
      snap.buttons[i] = b ? b.value : 0;
      snap.pressed[i] = b?.pressed ? 1 : 0;
    }
    for (let i = 0; i < snap.buttonCount; i++) {
      if (snap.pressed[i] === 1 && this.prevButtons[i] === 0) this.lastEdgeTime = now;
      this.prevButtons[i] = snap.pressed[i]!;
    }

    const f = this.frame;
    if (pad.mapping === "standard") {
      f.lx = snap.axes[0]!;
      f.ly = -snap.axes[1]!;
      f.rx = snap.axes[2]!;
      f.ry = -snap.axes[3]!;
      f.lt = snap.buttons[STD.lt]!;
      f.pull = snap.pressed[STD.top] === 1 ? 1 : 0;
      f.reach = Math.max(snap.buttons[STD.rt]!, snap.pressed[STD.left] === 1 ? 1 : 0);
      f.jump = snap.pressed[STD.bottom] === 1;
      f.tuck = snap.pressed[STD.right] === 1;
      f.restart = snap.pressed[STD.back] === 1;
      f.pause = snap.pressed[STD.start] === 1;
      f.dpadX = (snap.pressed[STD.dpadRight] === 1 ? 1 : 0) - (snap.pressed[STD.dpadLeft] === 1 ? 1 : 0);
      f.dpadY = (snap.pressed[STD.dpadUp] === 1 ? 1 : 0) - (snap.pressed[STD.dpadDown] === 1 ? 1 : 0);
      f.lb = snap.pressed[STD.lb] === 1;
      f.rb = snap.pressed[STD.rb] === 1;
      f.l3 = snap.pressed[STD.lstick] === 1;
      f.r3 = snap.pressed[STD.rstick] === 1;
    } else {
      const ident = identifyPad(pad.id);
      // Unknown non-standard pads: Switch family gets the hid-nintendo layout, everything else the xpad one.
      const m =
        deviceMappings[`${ident.vendor}:${ident.product}`] ??
        (ident.family === "switch" ? deviceMappings["057e:2009"]! : xpadRaw);
      const btn = (k: keyof typeof STD): number => {
        const i = m.buttons[k];
        return i === undefined ? 0 : (snap.buttons[i] ?? 0);
      };
      const down = (k: keyof typeof STD): boolean => {
        const i = m.buttons[k];
        return i === undefined ? false : snap.pressed[i] === 1;
      };
      f.lx = snap.axes[m.axes.lx] ?? 0;
      f.ly = -(snap.axes[m.axes.ly] ?? 0);
      f.rx = snap.axes[m.axes.rx] ?? 0;
      f.ry = -(snap.axes[m.axes.ry] ?? 0);
      let lt = btn("lt");
      let rt = btn("rt");
      if (m.triggerAxes) {
        const { lt: la, rt: ra, from, to } = m.triggerAxes;
        const norm = (v: number): number => Math.min(1, Math.max(0, (v - from) / (to - from)));
        lt = Math.max(lt, norm(la < snap.axisCount ? snap.axes[la]! : from));
        rt = Math.max(rt, norm(ra < snap.axisCount ? snap.axes[ra]! : from));
      }
      f.lt = lt;
      f.pull = down("top") ? 1 : 0;
      f.reach = Math.max(rt, down("left") ? 1 : 0);
      f.jump = down("bottom");
      f.tuck = down("right");
      f.restart = down("back");
      f.pause = down("start");
      f.lb = down("lb");
      f.rb = down("rb");
      f.l3 = down("lstick");
      f.r3 = down("rstick");
      if (m.dpadAxes) {
        const hx = snap.axes[m.dpadAxes.x] ?? 0;
        const hy = snap.axes[m.dpadAxes.y] ?? 0;
        f.dpadX = hx > 0.5 ? 1 : hx < -0.5 ? -1 : 0;
        f.dpadY = hy < -0.5 ? 1 : hy > 0.5 ? -1 : 0;
      } else {
        f.dpadX = (down("dpadRight") ? 1 : 0) - (down("dpadLeft") ? 1 : 0);
        f.dpadY = (down("dpadUp") ? 1 : 0) - (down("dpadDown") ? 1 : 0);
      }
    }
    return true;
  }

  private clear(): void {
    const snap = this.snapshot;
    snap.connected = false;
    snap.id = "";
    snap.index = -1;
    snap.family = "unknown";
    snap.axisCount = 0;
    snap.buttonCount = 0;
    snap.axes.fill(0);
    snap.buttons.fill(0);
    snap.pressed.fill(0);
    this.prevButtons.fill(0);
    const f = this.frame;
    f.lx = f.ly = f.rx = f.ry = f.reach = f.pull = f.lt = f.dpadX = f.dpadY = 0;
    f.jump = f.tuck = f.restart = f.pause = f.lb = f.rb = f.l3 = f.r3 = false;
  }
}

/** One pad the roster has seen: its API index, its family, and whether it is connected right now. */
export interface RosterPad {
  index: number;
  id: string;
  family: ControllerFamily;
  connected: boolean;
}

/** Pads the browser can list at once (the Gamepad API's own limit is four on most browsers). */
const ROSTER_SIZE = 16;

/**
 * Every pad at once, for the join screen of a split-screen race: which pads are connected, and
 * which pressed a button since the last poll (a press on a pad nobody holds joins a seat). Polling
 * allocates nothing; the `pressed` list is reused.
 */
export class GamepadRoster {
  readonly pads: RosterPad[] = [];
  /** API indices of the pads whose first button went down in the last poll, in index order. */
  readonly pressed: number[] = [];
  private readonly anyDown = new Uint8Array(ROSTER_SIZE);
  private readonly getPads: () => (Gamepad | null)[];

  constructor(getPads?: () => (Gamepad | null)[]) {
    this.getPads =
      getPads ??
      (() => (typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : []));
  }

  poll(): void {
    this.pressed.length = 0;
    for (const p of this.pads) p.connected = false;
    for (const pad of this.getPads()) {
      if (!pad?.connected || pad.index < 0 || pad.index >= ROSTER_SIZE) continue;
      let entry = this.pads.find((p) => p.index === pad.index);
      if (!entry) {
        entry = { index: pad.index, id: pad.id, family: identifyPad(pad.id).family, connected: true };
        this.pads.push(entry);
        this.pads.sort((a, b) => a.index - b.index);
      } else if (entry.id !== pad.id) {
        entry.id = pad.id;
        entry.family = identifyPad(pad.id).family;
      }
      entry.connected = true;
      let down = 0;
      for (const b of pad.buttons) {
        if (b.pressed) {
          down = 1;
          break;
        }
      }
      if (down === 1 && this.anyDown[pad.index] === 0) this.pressed.push(pad.index);
      this.anyDown[pad.index] = down;
    }
    for (const p of this.pads) if (!p.connected) this.anyDown[p.index] = 0;
  }

  /** The connected pads, by API index. */
  connected(): RosterPad[] {
    return this.pads.filter((p) => p.connected);
  }

  /** A short name for a pad ("Xbox pad 1"), numbered by its API index. */
  label(index: number): string {
    const pad = this.pads.find((p) => p.index === index);
    const family = pad?.family ?? "unknown";
    const name =
      family === "xbox"
        ? "Xbox"
        : family === "playstation"
          ? "PlayStation"
          : family === "switch"
            ? "Switch"
            : "";
    return `${name ? `${name} ` : ""}pad ${index + 1}`;
  }
}

/** Physical button names per family, by position. */
function names(family: ControllerFamily): {
  bottom: string;
  right: string;
  left: string;
  top: string;
  lb: string;
  rb: string;
  lt: string;
  rt: string;
} {
  switch (family) {
    case "playstation":
      return {
        bottom: "Cross",
        right: "Circle",
        left: "Square",
        top: "Triangle",
        lb: "L1",
        rb: "R1",
        lt: "L2",
        rt: "R2",
      };
    case "switch":
      return { bottom: "B", right: "A", left: "Y", top: "X", lb: "L", rb: "R", lt: "ZL", rt: "ZR" };
    case "xbox":
      return { bottom: "A", right: "B", left: "X", top: "Y", lb: "LB", rb: "RB", lt: "LT", rt: "RT" };
    default:
      return {
        bottom: "Bottom",
        right: "Right",
        left: "Left",
        top: "Top",
        lb: "LB",
        rb: "RB",
        lt: "LT",
        rt: "RT",
      };
  }
}

/**
 * Glyph labels for the four body actions: the face-button aliases and the trigger, plus what the
 * scheme adds on the bumpers, the left trigger, and the right stick. The stick up and down actions
 * are not listed; the HUD names them separately.
 */
export function glyphs(
  family: ControllerFamily,
  scheme?: PadScheme,
): {
  jump: string;
  tuck: string;
  reach: string;
  pull: string;
  /** What rotates the body with the left hand, empty unless the scheme has a rotate modifier. */
  rotate: string;
} {
  const n = names(family);
  const s = scheme ?? padScheme(DEFAULT_PAD_SCHEME);
  const jump: string[] = [n.bottom];
  const tuck: string[] = [n.right];
  const reach: string[] = [n.rt, n.left];
  const pull: string[] = [];
  const rotate = s.lt === "rotate" ? `${n.lt} + LS` : "";
  if (s.lt === "pull") pull.push(n.lt);
  else if (s.lt === "tuck") tuck.push(n.lt);
  pull.push(n.top);
  for (const [button, action] of [
    [n.lb, s.lb],
    [n.rb, s.rb],
  ] as const) {
    if (action === "jump") jump.push(button);
    else if (action === "tuck") tuck.push(button);
    else if (action === "pull") pull.push(button);
  }
  if (s.rsUp === "reach") reach.push("RS up");
  if (s.rsDown === "tuck") tuck.push("RS down");
  else if (s.rsDown === "pull") pull.push("RS down");
  return {
    jump: jump.join(" / "),
    tuck: tuck.join(" / "),
    reach: reach.join(" / "),
    pull: pull.join(" / "),
    rotate,
  };
}
